import React, { useState, useEffect } from "react";
import { AvatarCustomProp } from "./types";
import Avatar from "@mui/material/Avatar";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import {
  StyledBadgeLarge,
  StyledBadgeMedium,
  StyledBadgeSmall,
  StyledBadgeLargeLogo,
  StyledBadgeMediumLogo,
  StyledBadgeSmallLogo,
} from "./styles";
import { getProfilePicture } from "../../services/user/getProfilePicture";
import { postProfilePicture } from "../../services/user/postProfilePicture";
// import { postFiles, putProfilePicture, getProfilePicture } from "services";
import { useToasts } from "react-toast-notifications";
import { Box, CircularProgress } from "@mui/material";
import { Check } from "@mui/icons-material";
import theme from "../../theme/theme";

const AvatarUser: React.FC<AvatarCustomProp> = ({
  size,
  edit,
  hasLogo,
  setImage = "",
  setLogo = "",
}) => {
  const [selectedImage, setSelectedImage] = useState<string | undefined>(
    undefined
  );
  const [currentDate, setCurrentDate] = useState<number | null>(null);
  const [serverResponse, setServerResponse] = React.useState(false); // Server response state
  const [loading, setLoading] = React.useState(false); // Loading state
  const [success, setSuccess] = React.useState(false); // Success state
  const { addToast } = useToasts();
  const idUser = localStorage.getItem("userId");

  // ------------------- JWT -------------------
  const jwt = localStorage.getItem("jwt");

  // ------------------- GET RESOURCES FROM API -------------------
  const getPicture = async () => {
    await getProfilePicture(jwt!)
      .then((res) => {
        if (res.status !== 200) {
          setServerResponse(false);
        } else {
          setServerResponse(true);
          setSelectedImage(res.data.url);
        }
      })
      .catch((err) => {});
  };

  const uploadPicture = async (file: any): Promise<string> => {
    try {
      // if (selectedImage) {
      //   await putProfilePicture(jwt!, file);

      // } else {
      //   await postProfilePicture(jwt!, file);
      // }
      await postProfilePicture(jwt!, file);
      window.location.reload();
      return "success"; // Devuelve "success" en caso de éxito
    } catch (err) {
      setServerResponse(true); // Ajusta el estado según sea necesario
      return "error"; // Devuelve "error" en caso de error
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setCurrentDate(Date.now());
      getPicture();
    };
    fetchData();
  }, []);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setLoading(true);
      const reader = new FileReader();

      reader.onloadend = async () => {
        setSelectedImage(reader.result as string);
        const uploadResult = await uploadPicture(file);
        if (uploadResult === "success") {
          setLoading(false);
          addToast("Se actualizó la foto de perfil", {
            appearance: "success",
            autoDismiss: true,
          });
        } else {
          setLoading(false);
          addToast("Hubo un error al actualizar la foto de perfil", {
            appearance: "error",
            autoDismiss: true,
          });
        }
      };

      setTimeout(() => {
        setSuccess(false);
        getPicture();
      }, 5000);
      reader.readAsDataURL(file);
    }
  };

  if (edit) {
    if (size === "Large") {
      return (
        <Box sx={{ m: 1, position: "relative" }}>
          <StyledBadgeLarge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <IconButton
                aria-label="upload picture"
                component="label"
                sx={{
                  backgroundColor: success
                    ? theme.color.lemon
                    : theme.color.navy,
                  height: "55px",
                  width: "55px",
                }}
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageUpload}
                />
                {success ? (
                  <Check sx={{ fontSize: "100%", color: "#ffffff" }} />
                ) : (
                  <EditIcon sx={{ fontSize: "100%", color: "#ffffff" }} />
                )}
              </IconButton>
            }
          >
            {loading && (
              <CircularProgress
                size={68}
                sx={{
                  color: theme.color.lemon,
                  position: "absolute",
                  top: "224px",
                  left: "224px",
                  zIndex: 10,
                }}
              />
            )}
            <Avatar
              src={selectedImage}
              sx={{ width: 300, height: 300 }}
            ></Avatar>
          </StyledBadgeLarge>
        </Box>
      );
    }
    if (size === "Medium") {
      return (
        <StyledBadgeMedium
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <IconButton
              color="primary"
              aria-label="upload picture"
              component="label"
            >
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={handleImageUpload}
              />
              <EditIcon sx={{ fontSize: "85%", color: "#ffffff" }} />
            </IconButton>
          }
        >
          <Avatar src={selectedImage} sx={{ width: 115, height: 115 }}></Avatar>
        </StyledBadgeMedium>
      );
    }
    return (
      <StyledBadgeSmall
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={
          <IconButton
            color="primary"
            aria-label="upload picture"
            component="label"
          >
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={handleImageUpload}
            />
            <EditIcon sx={{ fontSize: "65%", color: "#ffffff" }} />
          </IconButton>
        }
      >
        <Avatar src={selectedImage} sx={{ width: 50, height: 50 }}></Avatar>
      </StyledBadgeSmall>
    );
  }

  if (hasLogo) {
    if (size === "Large") {
      return (
        <StyledBadgeLargeLogo
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <Avatar
              src={setLogo}
              sx={{ width: 225 * 0.25, height: 225 * 0.25 }}
            />
          }
        >
          <Avatar src={setImage} sx={{ width: 225, height: 225 }}></Avatar>
        </StyledBadgeLargeLogo>
      );
    }
    if (size === "Medium") {
      return (
        <StyledBadgeMediumLogo
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <Avatar
              src={setLogo}
              sx={{ width: 115 * 0.35, height: 115 * 0.35 }}
            />
          }
        >
          <Avatar src={setImage} sx={{ width: 115, height: 115 }}></Avatar>
        </StyledBadgeMediumLogo>
      );
    }
    return (
      <StyledBadgeSmallLogo
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={
          <Avatar src={setLogo} sx={{ width: 50 * 0.5, height: 50 * 0.5 }} />
        }
      >
        <Avatar src={setImage} sx={{ width: 50, height: 50 }}></Avatar>
      </StyledBadgeSmallLogo>
    );
  }

  if (size === "Large") {
    return <Avatar src={setImage} sx={{ width: 225, height: 225 }}></Avatar>;
  }
  if (size === "Medium") {
    return <Avatar src={setImage} sx={{ width: 115, height: 115 }}></Avatar>;
  }
  return <Avatar src={setImage} sx={{ width: 50, height: 50 }}></Avatar>;
};

export default AvatarUser;
